import { apiCall } from './api';
import {
  BookingResponseSchema,
  DisputeResponseSchema,
  PackBalanceResponseSchema,
  PackPurchaseResponseSchema,
  TutorSlotSchema,
} from './schemas/booking';
import type {
  AcceptBookingInput,
  BookingResponse,
  CreateBookingInput,
  CreateRecurringSeriesInput,
  DeclineBookingInput,
  DisputeResponse,
  FileDisputeInput,
  PackBalanceResponse,
  PackPurchaseResponse,
  PurchasePackInput,
  TutorSlot,
} from './schemas/booking';
import { PublicTutorCardSchema } from './schemas/tutor-profile';
import type { PublicTutorCard } from './schemas/tutor-profile';

export async function createBooking(
  accessToken: string,
  body: CreateBookingInput,
): Promise<BookingResponse> {
  const res = await apiCall<unknown>('/bookings', {
    accessToken,
    method: 'POST',
    body,
  });
  return BookingResponseSchema.parse(res.data);
}

export async function createRecurringSeries(
  accessToken: string,
  body: CreateRecurringSeriesInput,
): Promise<BookingResponse[]> {
  const res = await apiCall<unknown>('/bookings/series', {
    accessToken,
    method: 'POST',
    body,
  });
  return BookingResponseSchema.array().parse(res.data);
}

export async function fetchTutorSlots(
  accessToken: string,
  tutorId: string,
  from: string,
  to: string,
): Promise<TutorSlot[]> {
  const params = new URLSearchParams({ from, to });
  const res = await apiCall<unknown>(
    `/tutors/${encodeURIComponent(tutorId)}/slots?${params.toString()}`,
    { accessToken },
  );
  return TutorSlotSchema.array().parse(res.data);
}

export async function getMyBookings(
  accessToken: string,
): Promise<BookingResponse[]> {
  const res = await apiCall<unknown>('/bookings', { accessToken });
  return BookingResponseSchema.array().parse(res.data);
}

export async function purchasePack(
  accessToken: string,
  body: PurchasePackInput,
): Promise<PackPurchaseResponse> {
  const res = await apiCall<unknown>('/packs', {
    accessToken,
    method: 'POST',
    body,
  });
  return PackPurchaseResponseSchema.parse(res.data);
}

export async function acceptBooking(
  accessToken: string,
  id: string,
  body: AcceptBookingInput,
): Promise<BookingResponse> {
  const res = await apiCall<unknown>(`/bookings/${encodeURIComponent(id)}/accept`, {
    accessToken,
    method: 'PATCH',
    body,
  });
  return BookingResponseSchema.parse(res.data);
}

export async function declineBooking(
  accessToken: string,
  id: string,
  body: DeclineBookingInput,
): Promise<BookingResponse> {
  const res = await apiCall<unknown>(`/bookings/${encodeURIComponent(id)}/decline`, {
    accessToken,
    method: 'PATCH',
    body,
  });
  return BookingResponseSchema.parse(res.data);
}

export async function giveConsent(
  accessToken: string,
  id: string,
): Promise<BookingResponse> {
  const res = await apiCall<unknown>(`/bookings/${encodeURIComponent(id)}/consent`, {
    accessToken,
    method: 'POST',
    body: {},
  });
  return BookingResponseSchema.parse(res.data);
}

export async function fileDispute(
  accessToken: string,
  body: FileDisputeInput,
): Promise<DisputeResponse> {
  const res = await apiCall<unknown>('/disputes', {
    accessToken,
    method: 'POST',
    body,
  });
  return DisputeResponseSchema.parse(res.data);
}

export async function getMyDisputes(
  accessToken: string,
): Promise<DisputeResponse[]> {
  const res = await apiCall<unknown>('/disputes', { accessToken });
  return DisputeResponseSchema.array().parse(res.data);
}

export async function fetchSubstitutes(
  accessToken: string,
  bookingId: string,
): Promise<PublicTutorCard[]> {
  const res = await apiCall<unknown>(`/bookings/${encodeURIComponent(bookingId)}/substitutes`, {
    accessToken,
  });
  return PublicTutorCardSchema.array().parse(res.data);
}

export async function getPackBalance(
  accessToken: string,
  tutorId: string,
): Promise<PackBalanceResponse> {
  const res = await apiCall<unknown>(`/packs/balance?tutorId=${encodeURIComponent(tutorId)}`, {
    accessToken,
  });
  return PackBalanceResponseSchema.parse(res.data);
}
